import { useEffect, useRef, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';

const SHOW_AFTER_MS = 120;
const HIDE_GRACE_MS = 220;

/**
 * Subtle top progress bar for route transitions (TanStack Router pending state).
 * Non-blocking: the previous page stays interactive until the next route is ready.
 * Uses router status only — no arbitrary navigation timeouts.
 */
export function RouteTransitionLoader() {
  const isPending = useRouterState({
    select: (s) => s.status === 'pending' || s.isLoading === true,
  });
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const showTimer = useRef<number | null>(null);
  const hideTimer = useRef<number | null>(null);

  useEffect(() => {
    if (showTimer.current) window.clearTimeout(showTimer.current);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);

    if (isPending) {
      setLeaving(false);
      // Avoid flicker on instant navigations.
      showTimer.current = window.setTimeout(() => setVisible(true), SHOW_AFTER_MS);
      return () => {
        if (showTimer.current) window.clearTimeout(showTimer.current);
      };
    }

    if (visible) {
      setLeaving(true);
      hideTimer.current = window.setTimeout(() => {
        setVisible(false);
        setLeaving(false);
      }, HIDE_GRACE_MS);
      return () => {
        if (hideTimer.current) window.clearTimeout(hideTimer.current);
      };
    }

    return undefined;
  }, [isPending, visible]);

  if (!visible) return null;

  return (
    <div
      className={`route-progress ${leaving ? 'is-leaving' : ''}`}
      role="status"
      aria-live="polite"
      aria-label="Loading page"
    >
      <div className="route-progress-bar" />
    </div>
  );
}
